/**
 * Spaces in Home's sidebar and in Chat: named containers for an
 * organization's chats (a client fund, a project), stored server-side per
 * organization (state/spaces → /api/amoreg/spaces). A space is one page,
 * `/$org/spaces/$spaceId` (components/spaces/SpacePage): the sidebar's rows,
 * the Spaces list and a chat's header crumb all open it, and New space lands
 * on it. The composer's space picker files (or moves) a chat.
 *
 * @module components/home/HomeSpaces
 */
import type { ThreadId } from "@amoreg/contracts";
import { Link, useNavigate } from "@tanstack/react-router";
import { CheckIcon, ChevronDownIcon } from "lucide-react";
import { useState, type FormEvent, type ReactElement, type ReactNode } from "react";

import { orgUrlSegment } from "../../orgRouting";
import { useActiveOrgId } from "../../state/orgs";
import {
  chooseThreadSpace,
  useOrgSpaces,
  useRequestedThreadSpaceId,
  type Space,
} from "../../state/spaces";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Menu, MenuGroup, MenuGroupLabel, MenuItem, MenuPopup, MenuSeparator, MenuTrigger } from "../ui/menu";
import { Popover, PopoverPopup, PopoverTitle, PopoverTrigger } from "../ui/popover";
import { Tip } from "../ui/tip";
import { toastManager } from "../ui/toast";
import { SpaceGlyph } from "../spaces/SpaceGlyph";

/**
 * New space: a small form in a popover over whatever `render` draws (the
 * sidebar's row, the Spaces page's button). A created space is announced and
 * opened on its own page.
 */
export function NewSpacePopover({
  render,
  children,
  side = "right",
  align = "start",
  tip,
  onNavigate,
}: {
  readonly render: ReactElement;
  readonly children: ReactNode;
  /** A tooltip for an icon-only trigger. */
  readonly tip?: string;
  readonly side?: "right" | "bottom";
  readonly align?: "start" | "end";
  /** Called before opening the new space, e.g. to close the mobile sidebar. */
  readonly onNavigate?: () => void;
}) {
  const orgId = useActiveOrgId();
  const { createSpace } = useOrgSpaces(orgId);
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [name, setName] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const submit = async (event: FormEvent) => {
    event.preventDefault();
    if (!name.trim() || busy) return;
    setBusy(true);
    setError(null);
    const result = await createSpace(name.trim());
    setBusy(false);
    if (!result.ok) {
      setError(result.message);
      return;
    }
    setOpen(false);
    setName("");
    toastManager.add({ type: "success", title: `Space “${result.space.name}” created` });
    if (orgId === null) return;
    onNavigate?.();
    void navigate({
      to: "/$org/spaces/$spaceId",
      params: { org: orgUrlSegment(orgId), spaceId: result.space.id },
    });
  };
  return (
    <Popover
      open={open}
      onOpenChange={(next) => {
        setOpen(next);
        if (!next) setError(null);
      }}
    >
      <Tip label={tip}>
        <PopoverTrigger render={render}>{children}</PopoverTrigger>
      </Tip>
      <PopoverPopup side={side} align={align} className="w-72">
        <form onSubmit={submit} className="flex flex-col gap-2.5" data-home-new-space-form="">
          <PopoverTitle className="text-[13px] font-medium">New space</PopoverTitle>
          <p className="text-xs text-muted-foreground">
            A space groups chats for a client, a fund or a project. Everyone in the organization sees it.
          </p>
          <Input
            autoFocus
            value={name}
            maxLength={60}
            placeholder="e.g. Orion Credit Fund"
            aria-label="Space name"
            onChange={(event) => setName(event.currentTarget.value)}
          />
          {error ? <p className="text-xs text-destructive">{error}</p> : null}
          <div className="flex justify-end gap-2">
            <Button type="button" size="sm" variant="ghost" onClick={() => setOpen(false)}>
              Cancel
            </Button>
            <Button type="submit" size="sm" disabled={!name.trim() || busy}>
              Create space
            </Button>
          </div>
        </form>
      </PopoverPopup>
    </Popover>
  );
}

/**
 * The chat's space: in the strip under the prompt box (`strip`), and as the
 * draft hero's "What should we work on in …?" (`headline`). A new chat files
 * into the chosen space once its thread exists (state/spaces
 * requestThreadSpace); a started chat moves at once. No space reads as the
 * organization itself, so `orgLabel` (its mark and name) is the trigger's
 * label until a space is chosen, and the menu's first choice. On servers
 * without spaces both show only the org's name.
 */
export function ComposerSpacePicker({
  threadId,
  draft,
  orgLabel,
  variant,
}: {
  readonly threadId: ThreadId;
  /** The chat has no server thread yet. */
  readonly draft: boolean;
  readonly orgLabel: ReactNode;
  readonly variant: "headline" | "strip";
}) {
  const orgId = useActiveOrgId();
  const { available, spaces, spaceIdByThreadId, assignThreadSpace } = useOrgSpaces(orgId);
  const requestedId = useRequestedThreadSpaceId(threadId);
  const currentId = draft ? requestedId : (spaceIdByThreadId.get(threadId) ?? requestedId);
  const current = spaces.find((space) => space.id === currentId) ?? null;
  const headline = variant === "headline";
  if (!available || orgId === null || (headline && spaces.length === 0)) {
    return headline ? (
      <Tip label={typeof orgLabel === "string" ? orgLabel : undefined}>
        <span className="inline-block max-w-64 truncate align-bottom">{orgLabel}</span>
      </Tip>
    ) : (
      <span className="inline-flex h-7 min-w-0 items-center gap-1.5 rounded-md border border-transparent px-[calc(--spacing(2)-1px)] text-sm font-medium text-muted-foreground/70 sm:h-6">
        {orgLabel}
      </span>
    );
  }
  const choose = async (space: Space | null) => {
    const error = await chooseThreadSpace({
      draft,
      threadId,
      organizationId: orgId,
      spaceId: space?.id ?? null,
      assign: assignThreadSpace,
    });
    if (error) toastManager.add({ type: "error", title: "Couldn’t move the chat", description: error });
  };
  return (
    <Menu>
      {headline ? (
        <Tip label={current?.name ?? (typeof orgLabel === "string" ? orgLabel : undefined)}>
          <MenuTrigger
            data-composer-space=""
            className="-mx-1 inline-flex max-w-72 cursor-pointer items-center gap-1 rounded-lg px-1 align-bottom outline-none transition-colors hover:bg-foreground/[0.06] focus-visible:ring-2 focus-visible:ring-ring data-[popup-open]:bg-foreground/[0.06]"
          >
            <span className="min-w-0 truncate">{current ? current.name : orgLabel}</span>
            <ChevronDownIcon className="size-5 shrink-0 opacity-40" />
          </MenuTrigger>
        </Tip>
      ) : (
        <MenuTrigger
          data-composer-space=""
          aria-label={current ? `Space: ${current.name}` : "Choose a space for this chat"}
          render={<Button variant="ghost" size="xs" />}
          className="min-w-0 justify-start font-medium text-muted-foreground/70 hover:text-foreground/80"
        >
          {current ? (
            <>
              <SpaceGlyph space={current} className="size-3 rounded-[3px] text-[7px]" />
              <span className="min-w-0 truncate">{current.name}</span>
            </>
          ) : (
            orgLabel
          )}
          <ChevronDownIcon className="size-3 shrink-0 opacity-50" />
        </MenuTrigger>
      )}
      <MenuPopup align={headline ? "center" : "start"} side={headline ? "bottom" : "top"} className="w-60">
        <MenuGroup>
          <MenuGroupLabel>{draft ? "Start the chat in" : "Move this chat to"}</MenuGroupLabel>
          <MenuItem onClick={() => void choose(null)} data-composer-space-none="">
            <span className="flex min-w-0 flex-1 items-center gap-1.5">{orgLabel}</span>
            {current === null ? <CheckIcon className="size-3.5" /> : null}
          </MenuItem>
          {spaces.length > 0 ? <MenuSeparator /> : null}
          {spaces.map((space) => (
            <MenuItem key={space.id} onClick={() => void choose(space)}>
              <SpaceGlyph space={space} />
              <span className="min-w-0 flex-1 truncate">{space.name}</span>
              {current?.id === space.id ? <CheckIcon className="size-3.5" /> : null}
            </MenuItem>
          ))}
        </MenuGroup>
      </MenuPopup>
    </Menu>
  );
}

/**
 * A chat header's space crumb ("Org / Space / title"): the thread's space,
 * linking to the space's page. Nothing when the chat is in no space or the
 * server has no spaces; the composer's space picker files and moves chats
 * (`ComposerSpacePicker`), so the crumb only shows where the chat lives.
 */
export function ThreadSpaceCrumb({ threadId }: { readonly threadId: ThreadId }) {
  const orgId = useActiveOrgId();
  const { available, spaces, spaceIdByThreadId } = useOrgSpaces(orgId);
  const current = spaces.find((space) => space.id === spaceIdByThreadId.get(threadId)) ?? null;
  if (!available || orgId === null || current === null) return null;
  return (
    <span className="inline-flex shrink-0 items-center gap-2">
      <Link
        to="/$org/spaces/$spaceId"
        params={{ org: orgUrlSegment(orgId), spaceId: current.id }}
        data-thread-space={current.id}
        aria-label={`Open the space ${current.name}`}
        className="inline-flex max-w-44 items-center gap-1.5 rounded-sm text-sm text-muted-foreground outline-none transition-colors hover:text-foreground focus-visible:ring-2 focus-visible:ring-ring"
      >
        <SpaceGlyph space={current} />
        <span className="truncate font-medium">{current.name}</span>
      </Link>
      <span aria-hidden className="text-icon-muted">
        /
      </span>
    </span>
  );
}
